import React, { useMemo } from 'react';

import {
  Grid, Stack, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import _, { toInteger } from 'lodash';

import ContentCard from '../../../components/ContentCard';
import SkeletonStack from '../../../components/SkeletonStack';
import { useGetCoursesQuery } from '../../../services/courses';
import { useGetEnrolmentSnapshotsQuery, useGetStudentSnapshotsQuery } from '../../../services/snapshots';
import { StudentState } from '../../../types/common';
import addDays from '../../../utils/common/addDays';
import useError from '../../../utils/react/hooks/useError';
import {
  EnrolmentCountOverTimeLineChart, StudentCountByGradYearBarChart,
} from '../ChartComponents';
import { ACTIVE_STUDENT_STATES } from '../constants';
import {
  ActiveStudentCount, CountsByCourse, SimplyCount,
} from '../types';
import { getChangeColor, getDateAttributes } from '../utils';

const END_OF_DAY = dayjs().endOf('day');

const OverviewTab = () => {
  const {
    data: studentSnapshots,
    isLoading: isStudentsSnapshotsLoading,
    isError: isStudentsSnapshotsError,
    error: studentSnapshotsError,
  } = useGetStudentSnapshotsQuery({});

  const {
    data: pastWeekEnrolmentSnapshots,
    isLoading: isPastWeekEnrolmentsSnapshotsLoading,
    isError: isPastWeekEnrolmentsSnapshotsError,
    error: pastWeekEnrolmentsSnapshotsError,
  } = useGetEnrolmentSnapshotsQuery({
    startDatetime: addDays(END_OF_DAY, -8).valueOf(),
    endDatetime: addDays(END_OF_DAY, -1).valueOf(),
  });

  const {
    data: currentEnrolmentSnapshot,
    isLoading: isCurrentEnrolmentSnapshotLoading,
    isError: isCurrentEnrolmentSnapshotError,
    error: currentEnrolmentSnapshotError,
  } = useGetEnrolmentSnapshotsQuery({ });

  const {
    data: courses,
    isLoading: isCoursesLoading,
    isError: isCoursesError,
    error: coursesError,
  } = useGetCoursesQuery({});

  const activeStudentCounts = useMemo(() => {
    const rawCounts = studentSnapshots?.data[0].countByGradYearThenState;

    if (rawCounts) {
      return Object.entries(rawCounts)
        .filter(([gradYear, states]) => gradYear && Object.keys(states).some(
          (state) => ACTIVE_STUDENT_STATES.includes(state as StudentState)))
        .map(([gradYear, states]) => ({
          gradYear,
          ...states,
        }) as ActiveStudentCount);
    }
    return undefined;
  }, [studentSnapshots?.data[0].countByGradYearThenState]);

  const pastWeekCourseCounts = useMemo(() => {
    if (pastWeekEnrolmentSnapshots?.data && currentEnrolmentSnapshot?.data && courses) {
      const dataIncludingCurrent = [
        ...pastWeekEnrolmentSnapshots.data,
        currentEnrolmentSnapshot.data[0],
      ];

      return dataIncludingCurrent.map((entry) => {
        const rawCounts = entry.countByGradYearThenState;

        const returnValue: CountsByCourse = {
          ...getDateAttributes(entry.datetime),
          counts: {},
        };

        Object.entries(rawCounts).forEach(([course, gradYears]) => {
          const subject = course.split('#')[0];
          const courseId = course.split('#')[1];
          let activeStudentCount = 0;

          Object.entries(gradYears).forEach(([, states]) => {
            Object.entries(states).forEach(([state, count]) => {
              if (ACTIVE_STUDENT_STATES.includes(state as StudentState)) {
                activeStudentCount += count;
              }
            });
          });

          returnValue.counts[course] = {
            label: courses.data.find((c) => c.subject === subject && c.courseId === courseId)?.shortLabel || 'Unknown',
            course,
            count: activeStudentCount,
          };
        });

        return returnValue;
      });
    }

    return undefined;
  }, [pastWeekEnrolmentSnapshots, currentEnrolmentSnapshot, courses]);

  const pastWeekTotalEnrolmentCounts = useMemo(() => {
    if (pastWeekCourseCounts) {
      return pastWeekCourseCounts.map((courseCounts) => ({
        ..._.pick(courseCounts, ['datetime', 'displayDate', 'displayDateWithYear']),
        count: Object.values(courseCounts.counts || {}).reduce(
          (acc, courseCount) => acc + courseCount.count, 0),
      })) as SimplyCount[];
    }

    return undefined;
  }, [pastWeekCourseCounts]);

  const totalEnrolmentChange = useMemo(() => {
    if (pastWeekTotalEnrolmentCounts) {
      const countToday = pastWeekTotalEnrolmentCounts[pastWeekTotalEnrolmentCounts.length - 1]
        .count;
      const countLastWeek = pastWeekTotalEnrolmentCounts[0].count;

      const change = (countToday - countLastWeek) / countLastWeek;

      return change;
    }

    return undefined;
  }, [pastWeekTotalEnrolmentCounts]);

  useError(isStudentsSnapshotsError, studentSnapshotsError);
  useError(isPastWeekEnrolmentsSnapshotsError, pastWeekEnrolmentsSnapshotsError);
  useError(isCurrentEnrolmentSnapshotError, currentEnrolmentSnapshotError);
  useError(isCoursesError, coursesError);

  if (isStudentsSnapshotsLoading
    || isPastWeekEnrolmentsSnapshotsLoading
    || isCurrentEnrolmentSnapshotLoading
    || isCoursesLoading
    || !activeStudentCounts
    || !pastWeekCourseCounts
    || !pastWeekTotalEnrolmentCounts
    // totalEnrolmentChange could be 0
    || totalEnrolmentChange === undefined) {
    return <SkeletonStack />;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <ContentCard title="Currently Active Students" variant="outlined">
          <StudentCountByGradYearBarChart data={activeStudentCounts} />
        </ContentCard>
      </Grid>
      <Grid item xs={12} sm={6}>
        <ContentCard title="Enrolments Over 7 Days" variant="outlined">
          <EnrolmentCountOverTimeLineChart
            data={pastWeekTotalEnrolmentCounts}
            // Don't plot over the year-included date, since this only shows 7 days anyways
            xAxisDataKey="displayDate"
          />
        </ContentCard>
      </Grid>
      <Grid item xs={12} sm={2}>
        <ContentCard title="Enrolments" variant="contained">
          <Stack spacing={1} direction="row">
            <Typography variant="h4">{pastWeekTotalEnrolmentCounts[pastWeekTotalEnrolmentCounts.length - 1].count}</Typography>
            <Typography
              variant="overline"
              color={getChangeColor(totalEnrolmentChange)}
            >
              {toInteger(totalEnrolmentChange * 100)}%
            </Typography>
          </Stack>
        </ContentCard>
      </Grid>
      {Object.values(pastWeekCourseCounts[pastWeekCourseCounts.length - 1].counts || {})
        .sort((a, b) => a.course.localeCompare(b.course))
        .map((courseCount) => {
          const courseCountOneWeekAgo = pastWeekCourseCounts[0].counts[courseCount.course];
          const change = (courseCount.count - courseCountOneWeekAgo.count)
              / courseCountOneWeekAgo.count;
          const percentageChange = toInteger(change * 100);

          return (
            <Grid item xs={12} sm={2} key={courseCount.course}>
              <ContentCard title={courseCount.label} variant="outlined">
                <Stack spacing={1} direction="row">
                  <Typography variant="h4">{courseCount.count}</Typography>
                  <Typography
                    variant="overline"
                    color={getChangeColor(change)}
                  >
                    {percentageChange}%
                  </Typography>
                </Stack>
              </ContentCard>
            </Grid>
          );
        })}
    </Grid>
  );
};
export default OverviewTab;
